import { useState } from 'react';
import { Shield, CheckCircle2, AlertCircle } from 'lucide-react';

import { changePassword } from '@/lib/api/auth';
import { cn } from '@/lib/cn';

function PasswordField({
  id,
  label,
  value,
  onChange,
  disabled,
  hasError,
  errorMessage,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  hasError: boolean;
  errorMessage?: string;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-[13px] font-medium text-foreground">
        {label}
      </label>
      <input
        id={id}
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="••••••••"
        className={cn(
          'h-9 w-full rounded-lg border bg-white px-3 text-[13px] text-foreground',
          'placeholder:text-muted-foreground/40 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          'disabled:cursor-not-allowed disabled:opacity-50',
          hasError ? 'border-destructive/70 focus:ring-destructive/20' : 'border-border focus:ring-ring/30',
        )}
      />
      {hasError && errorMessage && (
        <p className="flex items-center gap-1 text-[12px] text-destructive">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          {errorMessage}
        </p>
      )}
      {hint && !hasError && <p className="text-[11.5px] text-muted-foreground/60">{hint}</p>}
    </div>
  );
}

/** SecuritySettingsForm is the shared "change password" card. */
export function SecuritySettingsForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fieldError, setFieldError] = useState<{
    field: 'current' | 'next' | 'confirm' | 'general';
    message: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    setSuccess(false);

    if (!current) {
      setFieldError({ field: 'current', message: 'Please enter your current password.' });
      return;
    }
    if (next.length < 8) {
      setFieldError({ field: 'next', message: 'Must be at least 8 characters.' });
      return;
    }
    if (next !== confirm) {
      setFieldError({ field: 'confirm', message: 'Passwords do not match.' });
      return;
    }

    setLoading(true);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      setSuccess(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error;
      if (msg === 'current_password_incorrect') {
        setFieldError({ field: 'current', message: 'Current password is incorrect.' });
      } else {
        setFieldError({ field: 'general', message: 'Something went wrong. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2.5 border-b border-border px-5 py-3.5">
        <Shield className="h-4 w-4 text-muted-foreground/60" />
        <span
          className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60"
          style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
        >
          Security
        </span>
      </div>

      <div className="px-5 py-5">
        {success && (
          <div className="mb-5 flex items-center gap-2.5 rounded-xl bg-emerald-50 px-4 py-3 text-[13px] text-emerald-700 border border-emerald-100">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-500" />
            Password updated successfully.
          </div>
        )}
        {fieldError?.field === 'general' && (
          <div className="mb-5 flex items-center gap-2.5 rounded-xl bg-destructive/8 px-4 py-3 text-[13px] text-destructive border border-destructive/15">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {fieldError.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordField
            id="current-password"
            label="Current password"
            value={current}
            onChange={setCurrent}
            disabled={loading}
            hasError={fieldError?.field === 'current'}
            errorMessage={fieldError?.field === 'current' ? fieldError.message : undefined}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <PasswordField
              id="new-password"
              label="New password"
              value={next}
              onChange={setNext}
              disabled={loading}
              hasError={fieldError?.field === 'next'}
              errorMessage={fieldError?.field === 'next' ? fieldError.message : undefined}
              hint="Minimum 8 characters"
            />
            <PasswordField
              id="confirm-password"
              label="Confirm new password"
              value={confirm}
              onChange={setConfirm}
              disabled={loading}
              hasError={fieldError?.field === 'confirm'}
              errorMessage={fieldError?.field === 'confirm' ? fieldError.message : undefined}
            />
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={loading}
              className={cn(
                'rounded-lg px-5 py-2 text-[13px] font-medium transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'bg-foreground text-background hover:opacity-90',
              )}
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
